/**
 * Shared pixel-based render dimensions controls.
 *
 * Consumed by both `CameraPanel` (popover) and `RenderOutputPanel` (render
 * sidebar). Lets the user type raw width/height pixel values, quick-apply
 * a common preset from a dropdown, and swap orientation. The derived
 * aspect ratio is shown as a readout. The authoritative store state is
 * `renderWidth` / `renderHeight`; presets are transient actions.
 */

import type { ChangeEvent, CSSProperties, KeyboardEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { useStore } from "../app/store.ts";
import {
  DIMENSION_PRESETS,
  MAX_RENDER_DIM,
  MIN_RENDER_DIM,
  simplifyAspectRatio,
} from "../viewport/renderPresets.ts";
import { IconButton } from "./Button.tsx";
import {
  COLOR_BORDER_STRONG,
  COLOR_SURFACE,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_MUTED,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_SMALL,
  RADIUS_SM,
} from "./tokens.ts";

const SELECT_STYLE: CSSProperties = {
  flex: 1,
  padding: "3px 6px",
  borderRadius: RADIUS_SM,
  border: `0.5px solid ${COLOR_BORDER_STRONG}`,
  background: COLOR_SURFACE,
  color: COLOR_TEXT_SECONDARY,
  fontSize: FONT_SIZE_SMALL,
  cursor: "pointer",
};

const INPUT_STYLE: CSSProperties = {
  width: "100%",
  padding: "3px 6px",
  borderRadius: RADIUS_SM,
  border: `0.5px solid ${COLOR_BORDER_STRONG}`,
  background: COLOR_SURFACE,
  color: COLOR_TEXT_SECONDARY,
  fontSize: FONT_SIZE_SMALL,
  fontVariantNumeric: "tabular-nums",
  MozAppearance: "textfield",
};

const LABEL_STYLE: CSSProperties = {
  fontSize: FONT_SIZE_SMALL,
  color: COLOR_TEXT_MUTED,
  minWidth: 70,
};

const PRESET_PLACEHOLDER = "__placeholder__";

export function RenderDimensionsControls() {
  const renderWidth = useStore((s) => s.renderWidth);
  const renderHeight = useStore((s) => s.renderHeight);
  const setRenderDimensions = useStore((s) => s.setRenderDimensions);
  const setRenderWidth = useStore((s) => s.setRenderWidth);
  const setRenderHeight = useStore((s) => s.setRenderHeight);
  const renderTransparentBackground = useStore(
    (s) => s.renderTransparentBackground,
  );
  const setRenderTransparentBackground = useStore(
    (s) => s.setRenderTransparentBackground,
  );

  // Editable buffers decoupled from the store so the user can type
  // transient values without the store clamping mid-keystroke.
  const [wBuf, setWBuf] = useState(String(renderWidth));
  const [hBuf, setHBuf] = useState(String(renderHeight));

  // Re-sync buffers when the store value changes from the outside
  // (preset selection, swap, programmatic update).
  useEffect(() => setWBuf(String(renderWidth)), [renderWidth]);
  useEffect(() => setHBuf(String(renderHeight)), [renderHeight]);

  const commitWidth = useCallback(() => {
    const parsed = Number.parseInt(wBuf, 10);
    if (!Number.isFinite(parsed)) {
      setWBuf(String(renderWidth));
      return;
    }
    setRenderWidth(parsed);
  }, [wBuf, renderWidth, setRenderWidth]);

  const commitHeight = useCallback(() => {
    const parsed = Number.parseInt(hBuf, 10);
    if (!Number.isFinite(parsed)) {
      setHBuf(String(renderHeight));
      return;
    }
    setRenderHeight(parsed);
  }, [hBuf, renderHeight, setRenderHeight]);

  const onWidthKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
  };
  const onHeightKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
  };

  const applyPreset = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === PRESET_PLACEHOLDER) return;
    const idx = Number.parseInt(value, 10);
    const preset = DIMENSION_PRESETS[idx];
    if (preset) setRenderDimensions(preset.width, preset.height);
  };

  const swapOrientation = () => setRenderDimensions(renderHeight, renderWidth);

  const aspectReadout = formatAspect(renderWidth, renderHeight);

  // Reflect the current dimensions in the preset dropdown: if they match a
  // known preset, show it as selected; otherwise show the placeholder.
  const matchingPresetIdx = DIMENSION_PRESETS.findIndex(
    (p) => p.width === renderWidth && p.height === renderHeight,
  );
  const presetValue =
    matchingPresetIdx >= 0 ? String(matchingPresetIdx) : PRESET_PLACEHOLDER;

  return (
    <>
      {/* Dimensions row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 6,
        }}
      >
        <span style={LABEL_STYLE}>Dimensions</span>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1 }}>
          <input
            type="number"
            inputMode="numeric"
            min={MIN_RENDER_DIM}
            max={MAX_RENDER_DIM}
            step={1}
            value={wBuf}
            onChange={(e) => setWBuf(e.target.value)}
            onBlur={commitWidth}
            onKeyDown={onWidthKey}
            aria-label="Render width in pixels"
            style={INPUT_STYLE}
          />
          <IconButton
            type="button"
            onClick={swapOrientation}
            title="Swap width and height"
            icon={<span aria-hidden>⇄</span>}
            label="Swap width and height"
            variant="secondary"
            size="xs"
            style={{ padding: "3px 8px", lineHeight: 1 }}
          />
          <input
            type="number"
            inputMode="numeric"
            min={MIN_RENDER_DIM}
            max={MAX_RENDER_DIM}
            step={1}
            value={hBuf}
            onChange={(e) => setHBuf(e.target.value)}
            onBlur={commitHeight}
            onKeyDown={onHeightKey}
            aria-label="Render height in pixels"
            style={INPUT_STYLE}
          />
        </div>
      </div>

      {/* Presets row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span style={LABEL_STYLE}>Presets</span>
        <select
          value={presetValue}
          onChange={applyPreset}
          style={SELECT_STYLE}
          aria-label="Apply a preset render resolution"
        >
          <option value={PRESET_PLACEHOLDER}>—</option>
          {renderPresetGroup("Horizontal")}
          {renderPresetGroup("Square")}
          {renderPresetGroup("Vertical")}
        </select>
      </div>

      {/* Background */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span style={LABEL_STYLE}>Background</span>
        <select
          value={renderTransparentBackground ? "transparent" : "opaque"}
          onChange={(e) =>
            setRenderTransparentBackground(e.target.value === "transparent")
          }
          style={SELECT_STYLE}
        >
          <option value="transparent">Transparent</option>
          <option value="opaque">Opaque</option>
        </select>
      </div>

      {/* Derived aspect readout */}
      <div
        style={{
          fontSize: FONT_SIZE_SMALL,
          color: COLOR_TEXT_FAINT,
          marginTop: 2,
          marginBottom: 4,
        }}
      >
        {renderWidth} × {renderHeight} px · {aspectReadout}
      </div>
    </>
  );
}

function renderPresetGroup(group: "Horizontal" | "Square" | "Vertical") {
  const items = DIMENSION_PRESETS.filter((p) => p.group === group);
  if (items.length === 0) return null;
  return (
    <optgroup label={group} key={group}>
      {items.map((p) => {
        const idx = DIMENSION_PRESETS.indexOf(p);
        return (
          <option key={p.label} value={String(idx)}>
            {p.label} ({p.width}×{p.height})
          </option>
        );
      })}
    </optgroup>
  );
}

function formatAspect(width: number, height: number): string {
  const simplified = simplifyAspectRatio(width, height);
  const decimal = (width / height).toFixed(3);
  if (simplified) return `${decimal} (${simplified.w}:${simplified.h})`;
  return decimal;
}
